import { ReactNode, useRef } from 'react';
import { Box, Button, FlexGrid, FlexGridColumn, Icon, uniqueId } from '@hs-baumappe/legacy-ui';
import { useTranslation } from 'react-i18next';
import {
  createEmailFormValidationSchema,
  EmailForm as BaseEmailForm,
  EmailFormProps as BaseEmailFormProps,
} from '@hs-baumappe/forms';
import { RichTextEditorInstance } from '@hs-baumappe/rich-text-editor';

import useEmailFormSuggestions from './hooks/useEmailFormSuggestions';
import useEmailFormEditorUploadImage from './hooks/useEmailFormEditorUploadImage';
import yup from '../../yup';

type EmailFormBaseProps = Omit<
  BaseEmailFormProps,
  | 'id'
  | 'suggestions'
  | 'toEmailsInputProps'
  | 'ccEmailsInputProps'
  | 'subjectInputProps'
  | 'contentInputProps'
>;

interface EmailFormProps extends EmailFormBaseProps {
  projectId: string;
  loading: boolean;
  submitButtonText?: ReactNode;
}

export default function EmailForm({
  projectId,
  loading,
  submitButtonText,
  ...otherProps
}: EmailFormProps): JSX.Element {
  const { t } = useTranslation();
  const id = useRef(uniqueId('email-form-'));
  const editorRef = useRef<RichTextEditorInstance>(null);
  const uploadImage = useEmailFormEditorUploadImage(editorRef);
  const emailSuggestions = useEmailFormSuggestions(projectId);

  return (
    <FlexGrid>
      <FlexGridColumn sm={8}>
        <BaseEmailForm
          {...otherProps}
          id={id.current}
          suggestions={emailSuggestions}
          validationSchema={createEmailFormValidationSchema(yup)}
          toEmailsInputProps={{
            label: t('emailForm.fields.toEmail.label'),
            placeholder: t('emailForm.fields.toEmail.placeholder'),
          }}
          ccEmailsInputProps={{
            label: t('emailForm.fields.ccEmail.label'),
            placeholder: t('emailForm.fields.ccEmail.placeholder'),
          }}
          subjectInputProps={{
            label: t('emailForm.fields.subject.label'),
            placeholder: t('emailForm.fields.subject.placeholder'),
          }}
          contentInputProps={{
            label: t('emailForm.fields.content.label'),
            editorRef,
            enableResizeImage: true,
            onImageSelect: uploadImage,
            onImageDrop: uploadImage,
          }}
        />
      </FlexGridColumn>
      <FlexGridColumn sm={4}>
        <Box stylex={{ position: 'sticky', top: '0', paddingTop: 'small-x' }}>
          <Button
            form={id.current}
            type="submit"
            endIcon={<Icon name="email" />}
            color="primary"
            loading={loading}
            fullWidth
            className="qa-email-submit-button"
          >
            {submitButtonText ?? t('emailForm.submitButtonText')}
          </Button>
        </Box>
      </FlexGridColumn>
    </FlexGrid>
  );
}
